import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { 
  Palette, 
  Target, 
  Save, 
  RefreshCw, 
  Check,
  Building2,
  Type,
  Link,
  Image,
  Users,
  Plus,
  Trash2,
  ChevronDown,
  Settings2,
  Upload,
  X,
  Globe,
  Shield,
  Server,
  Edit,
  Eye,
  EyeOff,
  TestTube
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPER_ADMIN_EMAIL = "scaredkow@gmail.com";

// Permission options
const PERMISSIONS = [
  { value: "view_leads", label: "View Leads" },
  { value: "edit_leads", label: "Edit Leads" },
  { value: "delete_leads", label: "Delete Leads" },
  { value: "manage_users", label: "Manage Users" }
];

// User role options
const USER_ROLES = [
  { value: "user", label: "User" },
  { value: "org_admin", label: "Org Admin" }
];

export default function WhiteLabelSettings() {
  const { token, isAdmin, user } = useAuth();
  const { refreshTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("default-profile");
  const [uploading, setUploading] = useState(false);
  const [uploadedLogos, setUploadedLogos] = useState([]);
  const logoInputRef = useRef(null);
  const poweredByInputRef = useRef(null);
  
  // Check if current user is super admin
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  
  // Default profile settings state (for admin org)
  const [defaultProfile, setDefaultProfile] = useState({
    branding: {
      company_name: "Westbay",
      tagline: "Contract Hunter",
      description: "",
      logo_url: "",
      powered_by_logo: "",
      primary_color: "#2a9d8f",
      secondary_color: "#264653",
      accent_color: "#e9c46a",
      bg_color: "#1a3a42"
    },
    scoring_profile: {
      name: "Default Scoring",
      target_skills: [],
      categories: []
    }
  });
  const [adminEmails, setAdminEmails] = useState([]);
  const [presets, setPresets] = useState([]);
  
  // Organizations state (super admin only)
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [profileSubTab, setProfileSubTab] = useState("branding");
  
  // Scraper APIs state (super admin only)
  const [scraperApis, setScraperApis] = useState([]);
  const [availableScrapers, setAvailableScrapers] = useState([]);
  const [showApiModal, setShowApiModal] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  
  // New admin email input
  const [newAdminEmail, setNewAdminEmail] = useState("");

  useEffect(() => {
    fetchSettings();
    fetchPresets();
    fetchUploadedLogos();
    if (isSuperAdmin) {
      fetchOrganizations();
      fetchScraperApis();
      fetchAvailableScrapers();
    }
  }, [isSuperAdmin]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/tenant`);
      const data = await res.json();
      setDefaultProfile({
        branding: data.branding || defaultProfile.branding,
        scoring_profile: data.scoring_profile || defaultProfile.scoring_profile
      });
      setAdminEmails(data.admin_emails || []);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data);
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    }
  };

  const fetchOrgUsers = async (orgId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}/users`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrgUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch org users:", error);
    }
  };

  const fetchScraperApis = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/scraper-apis`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScraperApis(data);
      }
    } catch (error) {
      console.error("Failed to fetch scraper APIs:", error);
    }
  };

  const fetchAvailableScrapers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/available-scrapers`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableScrapers(data);
      }
    } catch (error) {
      console.error("Failed to fetch available scrapers:", error);
    }
  };

  const fetchPresets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/scoring-presets`);
      const data = await res.json();
      setPresets(data.presets || []);
    } catch (error) {
      console.error("Failed to fetch presets:", error);
    }
  };

  const fetchUploadedLogos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/uploaded-logos`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUploadedLogos(data.logos || []);
      }
    } catch (error) {
      console.error("Failed to fetch uploaded logos:", error);
    }
  };

  const handleLogoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await fetch(`${API_URL}/api/settings/upload-logo`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Upload failed");
      }
      
      const data = await res.json();
      toast.success("Logo uploaded successfully!");
      
      // Set the uploaded logo URL in branding
      if (type === "logo") {
        updateBrandingField("logo_url", data.url);
      } else if (type === "powered_by") {
        updateBrandingField("powered_by_logo_url", data.url);
      }
      
      fetchUploadedLogos();
    } catch (error) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setUploading(false);
      // Reset input
      if (type === "logo" && logoInputRef.current) logoInputRef.current.value = "";
      if (type === "powered_by" && poweredByInputRef.current) poweredByInputRef.current.value = "";
    }
  };

  const deleteUploadedLogo = async (logoId) => {
    try {
      const res = await fetch(`${API_URL}/api/settings/uploaded-logos/${logoId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Logo deleted");
        fetchUploadedLogos();
      }
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const saveDefaultProfile = async () => {
    setSaving(true);
    try {
      // Save branding
      const brandingRes = await fetch(`${API_URL}/api/settings/branding`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(defaultProfile.branding)
      });
      
      if (!brandingRes.ok) throw new Error("Failed to save branding");
      
      // Save scoring profile
      const scoringRes = await fetch(`${API_URL}/api/settings/scoring-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(defaultProfile.scoring_profile)
      });
      
      if (!scoringRes.ok) throw new Error("Failed to save scoring profile");
      
      toast.success("Default profile saved! Refreshing theme...");
      setTimeout(() => {
        refreshTheme();
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error("Failed to save default profile");
    } finally {
      setSaving(false);
    }
  };

  const updateDefaultBranding = (field, value) => {
    setDefaultProfile(prev => ({
      ...prev,
      branding: { ...prev.branding, [field]: value }
    }));
  };

  const updateDefaultScoring = (field, value) => {
    setDefaultProfile(prev => ({
      ...prev,
      scoring_profile: { ...prev.scoring_profile, [field]: value }
    }));
  };

  const applyPreset = async (presetId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/scoring-profile/apply-preset/${presetId}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error("Failed to apply preset");
      
      const data = await res.json();
      setDefaultProfile(prev => ({
        ...prev,
        scoring_profile: data.scoring_profile
      }));
      toast.success("Preset applied successfully!");
    } catch (error) {
      toast.error("Failed to apply preset");
    } finally {
      setSaving(false);
    }
  };

  const saveAdminEmails = async () => {
    if (adminEmails.length === 0) {
      toast.error("At least one admin email is required");
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/admin-emails`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ admin_emails: adminEmails })
      });
      
      if (!res.ok) throw new Error("Failed to save");
      
      toast.success("Admin emails saved!");
    } catch (error) {
      toast.error("Failed to save admin emails");
    } finally {
      setSaving(false);
    }
  };

  const addAdminEmail = () => {
    if (!newAdminEmail || !newAdminEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (adminEmails.includes(newAdminEmail.toLowerCase())) {
      toast.error("Email already exists");
      return;
    }
    setAdminEmails([...adminEmails, newAdminEmail.toLowerCase()]);
    setNewAdminEmail("");
  };

  const removeAdminEmail = (email) => {
    if (adminEmails.length === 1) {
      toast.error("Cannot remove the last admin");
      return;
    }
    setAdminEmails(adminEmails.filter(e => e !== email));
  };

  const updateBrandingField = (field, value) => {
    setDefaultProfile(prev => ({ 
      ...prev, 
      branding: { ...prev.branding, [field]: value }
    }));
  };

  const updateProfileField = (field, value) => {
    setDefaultProfile(prev => ({ 
      ...prev, 
      scoring_profile: { ...prev.scoring_profile, [field]: value }
    }));
  };

  const updateCategory = (index, field, value) => {
    const newCategories = [...(defaultProfile.scoring_profile?.categories || [])];
    newCategories[index] = { ...newCategories[index], [field]: value };
    setDefaultProfile(prev => ({ 
      ...prev, 
      scoring_profile: { ...prev.scoring_profile, categories: newCategories }
    }));
  };

  const updateCategoryKeywords = (index, keywordsStr) => {
    const keywords = keywordsStr.split(",").map(k => k.trim().toLowerCase()).filter(k => k);
    updateCategory(index, "keywords", keywords);
  };

  // ================== ORGANIZATION CRUD ==================
  const createOrganization = async (orgData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(orgData)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to create organization");
      }
      toast.success("Organization created!");
      setShowOrgModal(false);
      setEditingOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateOrganization = async (orgId, orgData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(orgData)
      });
      if (!res.ok) throw new Error("Failed to update organization");
      toast.success("Organization updated!");
      setShowOrgModal(false);
      setEditingOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteOrganization = async (orgId) => {
    if (!window.confirm("Delete this organization and all its data? This cannot be undone.")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete organization");
      toast.success("Organization deleted");
      if (selectedOrg?.id === orgId) setSelectedOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const updateOrgProfile = async (orgId, profileData) => {
    setSaving(true);
    try {
      // Ensure profile data is properly structured
      const cleanProfile = {
        user_limit: profileData?.user_limit || 10,
        allowed_scrapers: profileData?.allowed_scrapers || [],
        branding: profileData?.branding || {},
        scoring_profile: profileData?.scoring_profile || {}
      };
      
      console.log("Saving profile:", cleanProfile);  // Debug log
      
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(cleanProfile)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to update profile");
      }
      toast.success("Profile updated!");
      fetchOrganizations();
      
      // Update selected org with the response
      const updated = await res.json();
      if (selectedOrg?.id === orgId) {
        setSelectedOrg(prev => ({ ...prev, ...updated, _activeProfileTab: prev._activeProfileTab }));
      }
    } catch (error) {
      toast.error(error.message);
      console.error("Profile update error:", error);
    } finally {
      setSaving(false);
    }
  };

  // ================== ORG USER CRUD ==================
  const createOrgUser = async (orgId, userData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to create user");
      }
      toast.success("User created!");
      setShowUserModal(false);
      setEditingUser(null);
      fetchOrgUsers(orgId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateOrgUser = async (orgId, userId, userData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      if (!res.ok) throw new Error("Failed to update user");
      toast.success("User updated!");
      setShowUserModal(false);
      setEditingUser(null);
      fetchOrgUsers(orgId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteOrgUser = async (orgId, userId) => {
    if (!window.confirm("Delete this user?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/organizations/${orgId}/users/${userId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete user");
      toast.success("User deleted");
      fetchOrgUsers(orgId);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ================== SCRAPER API CRUD ==================
  const createScraperApi = async (apiData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/scraper-apis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to create scraper API");
      }
      toast.success("Scraper API created!");
      setShowApiModal(false);
      setEditingApi(null);
      fetchScraperApis();
      fetchAvailableScrapers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateScraperApi = async (apiId, apiData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/scraper-apis/${apiId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      });
      if (!res.ok) throw new Error("Failed to update scraper API");
      toast.success("Scraper API updated!");
      setShowApiModal(false);
      setEditingApi(null);
      fetchScraperApis();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteScraperApi = async (apiId) => {
    if (!window.confirm("Delete this scraper API?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/scraper-apis/${apiId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete scraper API");
      toast.success("Scraper API deleted");
      fetchScraperApis();
      fetchAvailableScrapers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const testScraperApi = async (apiId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/scraper-apis/${apiId}/test`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Connection successful! (HTTP ${data.status_code})`);
      } else {
        toast.error(`Connection failed: ${data.message}`);
      }
    } catch (error) {
      toast.error("Test failed: " + error.message);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#e76f51]">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#2a9d8f] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whitelabel-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62]">
            <Settings2 className="w-6 h-6 text-[#faf3e0]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#faf3e0]">White-Label Settings</h1>
            <p className="text-sm text-[#a8c8c4]">Configure branding and scoring for your organization</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2a9d8f]/30 pb-2 overflow-x-auto">
        {[
          { id: "default-profile", label: "Default Profile", icon: Palette },
          { id: "admins", label: "Admin Users", icon: Users },
          ...(isSuperAdmin ? [
            { id: "organizations", label: "Organizations", icon: Building2 },
            { id: "scrapers", label: "Scraper APIs", icon: Server }
          ] : [])
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-[#2a9d8f] text-white"
                : "text-[#a8c8c4] hover:bg-[#2a9d8f]/20"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Default Profile Tab - Branding & Scoring Combined */}
      {activeTab === "default-profile" && defaultProfile.branding && (
        <div className="space-y-6">
          {/* Sub-tabs for Branding and Scoring */}
          <div className="flex gap-2 border-b border-[#2a9d8f]/20 pb-2">
            {[
              { id: "branding", label: "Branding", icon: Palette },
              { id: "scoring", label: "Scoring Profile", icon: Target }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setProfileSubTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                  profileSubTab === tab.id
                    ? "bg-[#e9c46a]/20 text-[#e9c46a] border border-[#e9c46a]/40"
                    : "text-[#a8c8c4] hover:bg-[#2a9d8f]/20"
                }`}
              >
                <tab.icon className="w-3 h-3" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Branding Sub-tab */}
          {profileSubTab === "branding" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Info */}
                <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
                  <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Company Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-[#a8c8c4] mb-1">Company Name</label>
                      <Input
                        value={defaultProfile.branding.company_name || ""}
                        onChange={(e) => updateBrandingField("company_name", e.target.value)}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    data-testid="input-company-name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Tagline</label>
                  <Input
                    value={defaultProfile.branding.tagline || ""}
                    onChange={(e) => updateBrandingField("tagline", e.target.value)}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    data-testid="input-tagline"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Description</label>
                  <Textarea
                    value={defaultProfile.branding.description || ""}
                    onChange={(e) => updateBrandingField("description", e.target.value)}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    rows={3}
                    data-testid="input-description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Footer Company</label>
                  <Input
                    value={defaultProfile.branding.footer_company || ""}
                    onChange={(e) => updateBrandingField("footer_company", e.target.value)}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    data-testid="input-footer-company"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Footer Year</label>
                  <Input
                    type="number"
                    value={defaultProfile.branding.footer_year || 2026}
                    onChange={(e) => updateBrandingField("footer_year", parseInt(e.target.value))}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    data-testid="input-footer-year"
                  />
                </div>
              </div>
            </div>

            {/* Logos & Links */}
            <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
              <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
                <Image className="w-5 h-5" />
                Logos & Links
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Logo URL</label>
                  <div className="flex gap-2">
                    <Input
                      value={defaultProfile.branding.logo_url || ""}
                      onChange={(e) => updateBrandingField("logo_url", e.target.value)}
                      className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                      placeholder="https://..."
                      data-testid="input-logo-url"
                    />
                    <input
                      type="file"
                      ref={logoInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, "logo")}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploading}
                      className="border-[#2a9d8f]/50 text-[#2a9d8f] hover:bg-[#2a9d8f]/20"
                      data-testid="upload-logo-btn"
                    >
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                  {defaultProfile.branding.logo_url && (
                    <img src={defaultProfile.branding.logo_url} alt="Logo preview" className="mt-2 h-12 rounded" />
                  )}
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Powered By Logo URL</label>
                  <div className="flex gap-2">
                    <Input
                      value={defaultProfile.branding.powered_by_logo_url || ""}
                      onChange={(e) => updateBrandingField("powered_by_logo_url", e.target.value)}
                      className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                      placeholder="https://..."
                      data-testid="input-powered-by-logo"
                    />
                    <input
                      type="file"
                      ref={poweredByInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, "powered_by")}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => poweredByInputRef.current?.click()}
                      disabled={uploading}
                      className="border-[#2a9d8f]/50 text-[#2a9d8f] hover:bg-[#2a9d8f]/20"
                      data-testid="upload-powered-by-btn"
                    >
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                  {defaultProfile.branding.powered_by_logo_url && (
                    <img src={defaultProfile.branding.powered_by_logo_url} alt="Powered by preview" className="mt-2 h-10 rounded" />
                  )}
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Powered By Text</label>
                  <Input
                    value={defaultProfile.branding.powered_by_text || ""}
                    onChange={(e) => updateBrandingField("powered_by_text", e.target.value)}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    data-testid="input-powered-by-text"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#a8c8c4] mb-1">Powered By Link</label>
                  <Input
                    value={defaultProfile.branding.powered_by_link || ""}
                    onChange={(e) => updateBrandingField("powered_by_link", e.target.value)}
                    className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                    placeholder="https://..."
                    data-testid="input-powered-by-link"
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30 lg:col-span-2">
              <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Brand Colors
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: "primary_color", label: "Primary" },
                  { key: "secondary_color", label: "Secondary" },
                  { key: "accent_color", label: "Accent" },
                  { key: "danger_color", label: "Danger" },
                  { key: "background_dark", label: "Bg Dark" },
                  { key: "background_darker", label: "Bg Darker" },
                  { key: "text_light", label: "Text Light" },
                  { key: "text_muted", label: "Text Muted" }
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-sm text-[#a8c8c4] mb-1">{label}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={defaultProfile.branding[key] || "#000000"}
                        onChange={(e) => updateBrandingField(key, e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                        data-testid={`color-${key}`}
                      />
                      <Input
                        value={defaultProfile.branding[key] || ""}
                        onChange={(e) => updateBrandingField(key, e.target.value)}
                        className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Button
            onClick={saveDefaultProfile}
            disabled={saving}
            className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] text-white"
            data-testid="save-branding-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Default Profile
          </Button>
          </div>
          )}

          {/* Scoring Profile Sub-tab */}
          {profileSubTab === "scoring" && defaultProfile.scoring_profile && (
            <div className="space-y-6">
              {/* Presets */}
              <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
                <h3 className="text-lg font-semibold text-[#e9c46a] mb-4">Quick Presets</h3>
                <div className="flex flex-wrap gap-2">
                  {presets.map(preset => (
                    <Button
                      key={preset.id}
                      variant="outline"
                      onClick={() => applyPreset(preset.id)}
                      disabled={saving}
                      className={`border-[#2a9d8f]/50 text-[#a8c8c4] hover:bg-[#2a9d8f]/20 ${
                        defaultProfile.scoring_profile?.id === preset.id ? "bg-[#2a9d8f]/30 border-[#2a9d8f]" : ""
                      }`}
                      data-testid={`preset-${preset.id}`}
                    >
                      {defaultProfile.scoring_profile?.id === preset.id && <Check className="w-4 h-4 mr-1" />}
                      {preset.name}
                    </Button>
                  ))}
                </div>
              </div>

          {/* Profile Info */}
          <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
            <h3 className="text-lg font-semibold text-[#e9c46a] mb-4">Profile Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#a8c8c4] mb-1">Profile Name</label>
                <Input
                  value={defaultProfile.scoring_profile?.name || ""}
                  onChange={(e) => updateProfileField("name", e.target.value)}
                  className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                  data-testid="input-profile-name"
                />
              </div>
              <div>
                <label className="block text-sm text-[#a8c8c4] mb-1">Description</label>
                <Input
                  value={defaultProfile.scoring_profile?.description || ""}
                  onChange={(e) => updateProfileField("description", e.target.value)}
                  className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                  data-testid="input-profile-desc"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-[#a8c8c4] mb-1">Target Skills (comma-separated)</label>
                <Input
                  value={(defaultProfile.scoring_profile.target_skills || []).join(", ")}
                  onChange={(e) => updateProfileField("target_skills", e.target.value.split(",").map(s => s.trim()).filter(s => s))}
                  className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                  placeholder="AWS, DevSecOps, AI, ..."
                  data-testid="input-target-skills"
                />
              </div>
            </div>
          </div>

          {/* Scoring Categories */}
          <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
            <h3 className="text-lg font-semibold text-[#e9c46a] mb-4">Scoring Categories</h3>
            <div className="space-y-4">
              {(defaultProfile.scoring_profile.categories || []).map((category, index) => (
                <div key={category.id || index} className="p-4 rounded-lg bg-[#264653]/50 border border-[#2a9d8f]/20">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-[#a8c8c4] mb-1">Name</label>
                      <Input
                        value={category.name || ""}
                        onChange={(e) => updateCategory(index, "name", e.target.value)}
                        className="bg-[#1a3a42] border-[#2a9d8f]/50 text-[#faf3e0] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#a8c8c4] mb-1">Description</label>
                      <Input
                        value={category.description || ""}
                        onChange={(e) => updateCategory(index, "description", e.target.value)}
                        className="bg-[#1a3a42] border-[#2a9d8f]/50 text-[#faf3e0] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#a8c8c4] mb-1">Max Points</label>
                      <Input
                        type="number"
                        value={category.max_points || 20}
                        onChange={(e) => updateCategory(index, "max_points", parseInt(e.target.value))}
                        className="bg-[#1a3a42] border-[#2a9d8f]/50 text-[#faf3e0] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#a8c8c4] mb-1">Keywords (comma-separated)</label>
                      <Input
                        value={(category.keywords || []).join(", ")}
                        onChange={(e) => updateCategoryKeywords(index, e.target.value)}
                        className="bg-[#1a3a42] border-[#2a9d8f]/50 text-[#faf3e0] text-sm"
                        placeholder="keyword1, keyword2, ..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={saveDefaultProfile}
            disabled={saving}
            className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] text-white"
            data-testid="save-scoring-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Default Profile
          </Button>
        </div>
          )}
        </div>
      )}

      {/* Admin Users Tab */}
      {activeTab === "admins" && (
        <div className="space-y-6">
          <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
            <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Admin Email Addresses
            </h3>
            <p className="text-sm text-[#a8c8c4] mb-4">
              Users with these email addresses will automatically have admin privileges and skip approval.
            </p>
            
            {/* Existing admins */}
            <div className="space-y-2 mb-4">
              {adminEmails.map((email, index) => (
                <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-[#264653]/50">
                  <span className="flex-1 text-[#faf3e0]">{email}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAdminEmail(email)}
                    className="text-[#e76f51] hover:bg-[#e76f51]/20"
                    data-testid={`remove-admin-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            
            {/* Add new admin */}
            <div className="flex gap-2">
              <Input
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="admin@example.com"
                className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                data-testid="input-new-admin-email"
              />
              <Button
                onClick={addAdminEmail}
                className="bg-[#2a9d8f] text-white"
                data-testid="add-admin-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>

          <Button
            onClick={saveAdminEmails}
            disabled={saving}
            className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] text-white"
            data-testid="save-admins-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Admin Emails
          </Button>
        </div>
      )}

      {/* Organizations Tab (Super Admin Only) */}
      {activeTab === "organizations" && isSuperAdmin && (
        <div className="space-y-6">
          {/* Organizations List */}
          <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#e9c46a] flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organizations
              </h3>
              <Button
                onClick={() => { setEditingOrg(null); setShowOrgModal(true); }}
                className="bg-[#2a9d8f] text-white"
                data-testid="add-org-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Organization
              </Button>
            </div>
            
            {organizations.length === 0 ? (
              <p className="text-[#a8c8c4] text-center py-8">No organizations yet. Create your first one!</p>
            ) : (
              <div className="space-y-3">
                {organizations.map(org => (
                  <div 
                    key={org.id} 
                    className={`p-4 rounded-lg border transition-all cursor-pointer ${
                      selectedOrg?.id === org.id 
                        ? "bg-[#2a9d8f]/20 border-[#2a9d8f]" 
                        : "bg-[#264653]/50 border-[#2a9d8f]/20 hover:border-[#2a9d8f]/50"
                    }`}
                    onClick={() => { 
                      // Initialize profile structure when selecting org
                      const orgWithProfile = {
                        ...org,
                        profile: {
                          user_limit: org.profile?.user_limit || 10,
                          allowed_scrapers: org.profile?.allowed_scrapers || [],
                          branding: org.profile?.branding || {},
                          scoring_profile: org.profile?.scoring_profile || {}
                        }
                      };
                      setSelectedOrg(orgWithProfile); 
                      fetchOrgUsers(org.id); 
                    }}
                    data-testid={`org-${org.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[#faf3e0]">{org.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded ${org.is_active ? "bg-[#2a9d8f]/30 text-[#2a9d8f]" : "bg-[#e76f51]/30 text-[#e76f51]"}`}>
                            {org.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <p className="text-sm text-[#a8c8c4]">
                          <Globe className="w-3 h-3 inline mr-1" />
                          {org.subdomain}.westbay.io
                        </p>
                        <p className="text-xs text-[#a8c8c4] mt-1">
                          {org.user_count || 0} users • Limit: {org.profile?.user_limit || 10}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setEditingOrg(org); setShowOrgModal(true); }}
                          className="text-[#e9c46a] hover:bg-[#e9c46a]/20"
                          data-testid={`edit-org-${org.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteOrganization(org.id); }}
                          className="text-[#e76f51] hover:bg-[#e76f51]/20"
                          data-testid={`delete-org-${org.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Org Profile Configuration - Expanded */}
          {selectedOrg && (
            <div className="space-y-4">
              {/* Profile Sub-tabs */}
              <div className="flex gap-2 border-b border-[#2a9d8f]/30 pb-2 overflow-x-auto">
                {[
                  { id: "users", label: "Users", icon: Users },
                  { id: "branding", label: "Branding", icon: Palette },
                  { id: "scrapers", label: "Scrapers", icon: Globe },
                  { id: "scoring", label: "Scoring Profile", icon: Target }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedOrg(prev => ({ ...prev, _activeProfileTab: tab.id }))}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all whitespace-nowrap ${
                      (selectedOrg._activeProfileTab || "users") === tab.id
                        ? "bg-[#2a9d8f] text-white"
                        : "text-[#a8c8c4] hover:bg-[#2a9d8f]/20"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Users Sub-tab */}
              {(selectedOrg._activeProfileTab || "users") === "users" && (
                <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#e9c46a] flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Users in {selectedOrg.name}
                    </h3>
                    <Button
                      onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                      className="bg-[#2a9d8f] text-white"
                      data-testid="add-user-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add User
                    </Button>
                  </div>
                  
                  <div className="mb-4 p-3 rounded-lg bg-[#264653]/50">
                    <label className="block text-sm text-[#a8c8c4] mb-1">User Limit</label>
                    <Input
                      type="number"
                      value={selectedOrg.profile?.user_limit || 10}
                      onChange={(e) => setSelectedOrg(prev => ({
                        ...prev,
                        profile: { ...prev.profile, user_limit: parseInt(e.target.value) || 10 }
                      }))}
                      className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] w-32"
                    />
                  </div>
                  
                  {orgUsers.length === 0 ? (
                    <p className="text-[#a8c8c4] text-center py-4">No users in this organization yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {orgUsers.map(user => (
                        <div key={user.id} className="flex items-center justify-between p-3 rounded-lg bg-[#264653]/50">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#faf3e0]">{user.name}</span>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                user.role === "org_admin" ? "bg-[#e9c46a]/30 text-[#e9c46a]" : "bg-[#2a9d8f]/30 text-[#2a9d8f]"
                              }`}>
                                {user.role === "org_admin" ? "Org Admin" : "User"}
                              </span>
                            </div>
                            <p className="text-sm text-[#a8c8c4]">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                              className="text-[#e9c46a] hover:bg-[#e9c46a]/20"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteOrgUser(selectedOrg.id, user.id)}
                              className="text-[#e76f51] hover:bg-[#e76f51]/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Scrapers Sub-tab */}
              {selectedOrg._activeProfileTab === "scrapers" && (
                <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
                  <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Allowed Scrapers for {selectedOrg.name}
                  </h3>
                  <p className="text-sm text-[#a8c8c4] mb-4">
                    Select which scrapers this organization can use. Unchecked scrapers will not be available to users.
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                    {availableScrapers.map(scraper => (
                      <label 
                        key={scraper.id} 
                        className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all ${
                          (selectedOrg.profile?.allowed_scrapers || []).includes(scraper.id)
                            ? "bg-[#2a9d8f]/20 border border-[#2a9d8f]"
                            : "bg-[#264653]/50 border border-[#2a9d8f]/20 hover:border-[#2a9d8f]/50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={(selectedOrg.profile?.allowed_scrapers || []).includes(scraper.id)}
                          onChange={(e) => {
                            const current = selectedOrg.profile?.allowed_scrapers || [];
                            const updated = e.target.checked 
                              ? [...current, scraper.id]
                              : current.filter(id => id !== scraper.id);
                            setSelectedOrg(prev => ({
                              ...prev,
                              profile: { ...prev.profile, allowed_scrapers: updated }
                            }));
                          }}
                          className="rounded text-[#2a9d8f]"
                        />
                        <div>
                          <span className="text-sm font-medium text-[#faf3e0]">{scraper.name}</span>
                          {scraper.description && (
                            <p className="text-xs text-[#a8c8c4]">{scraper.description}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  <Button
                    onClick={() => updateOrgProfile(selectedOrg.id, selectedOrg.profile)}
                    disabled={saving}
                    className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] text-white"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Scrapers
                  </Button>
                </div>
              )}

              {/* Branding Sub-tab */}
              {selectedOrg._activeProfileTab === "branding" && (
                <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
                  <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Branding for {selectedOrg.name}
                  </h3>
                  
                  {/* Use Default Toggle */}
                  <div className="mb-6 p-4 rounded-lg bg-[#264653]/50 border border-[#2a9d8f]/30">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!selectedOrg.profile?.branding?.use_custom}
                        onChange={(e) => setSelectedOrg(prev => ({
                          ...prev,
                          profile: { 
                            ...prev.profile, 
                            branding: { ...prev.profile?.branding, use_custom: !e.target.checked }
                          }
                        }))}
                        className="rounded text-[#2a9d8f] w-5 h-5"
                      />
                      <div>
                        <span className="text-[#faf3e0] font-medium">Use Default Profile Branding</span>
                        <p className="text-xs text-[#a8c8c4]">When checked, this organization will inherit branding from the default profile</p>
                      </div>
                    </label>
                  </div>
                  
                  {selectedOrg.profile?.branding?.use_custom !== false && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Company Info */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-[#e9c46a] flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Company Information
                      </h4>
                      <div>
                        <label className="block text-sm text-[#a8c8c4] mb-1">Company Name</label>
                        <Input
                          value={selectedOrg.profile?.branding?.company_name || ""}
                          onChange={(e) => setSelectedOrg(prev => ({
                            ...prev,
                            profile: { 
                              ...prev.profile, 
                              branding: { ...prev.profile?.branding, company_name: e.target.value }
                            }
                          }))}
                          className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                          placeholder="Acme Corp"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#a8c8c4] mb-1">Tagline</label>
                        <Input
                          value={selectedOrg.profile?.branding?.tagline || ""}
                          onChange={(e) => setSelectedOrg(prev => ({
                            ...prev,
                            profile: { 
                              ...prev.profile, 
                              branding: { ...prev.profile?.branding, tagline: e.target.value }
                            }
                          }))}
                          className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                          placeholder="Your Company Tagline"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#a8c8c4] mb-1">Logo</label>
                        <div className="space-y-2">
                          {selectedOrg.profile?.branding?.logo_url && (
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-[#264653]/50">
                              <img 
                                src={selectedOrg.profile.branding.logo_url} 
                                alt="Logo" 
                                className="w-12 h-12 object-contain rounded"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedOrg(prev => ({
                                  ...prev,
                                  profile: { 
                                    ...prev.profile, 
                                    branding: { ...prev.profile?.branding, logo_url: "" }
                                  }
                                }))}
                                className="text-[#e76f51] hover:bg-[#e76f51]/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Input
                              value={selectedOrg.profile?.branding?.logo_url || ""}
                              onChange={(e) => setSelectedOrg(prev => ({
                                ...prev,
                                profile: { 
                                  ...prev.profile, 
                                  branding: { ...prev.profile?.branding, logo_url: e.target.value }
                                }
                              }))}
                              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                              placeholder="https://... or upload"
                            />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              id={`org-logo-upload-${selectedOrg.id}`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append("file", file);
                                
                                try {
                                  const res = await fetch(`${API_URL}/api/upload/logo`, {
                                    method: "POST",
                                    headers: { "Authorization": `Bearer ${token}` },
                                    body: formData
                                  });
                                  const data = await res.json();
                                  if (data.url) {
                                    setSelectedOrg(prev => ({
                                      ...prev,
                                      profile: { 
                                        ...prev.profile, 
                                        branding: { ...prev.profile?.branding, logo_url: data.url }
                                      }
                                    }));
                                    toast.success("Logo uploaded!");
                                  }
                                } catch (err) {
                                  toast.error("Failed to upload logo");
                                }
                                e.target.value = "";
                              }}
                            />
                            <Button
                              variant="outline"
                              onClick={() => document.getElementById(`org-logo-upload-${selectedOrg.id}`).click()}
                              className="border-[#2a9d8f]/50 text-[#2a9d8f] hover:bg-[#2a9d8f]/20"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-[#e9c46a] flex items-center gap-2">
                        <Palette className="w-4 h-4" />
                        Brand Colors
                      </h4>
                      {[
                        { key: "primary_color", label: "Primary", default: "#2a9d8f" },
                        { key: "secondary_color", label: "Secondary", default: "#e9c46a" },
                        { key: "accent_color", label: "Accent", default: "#f4a261" },
                        { key: "bg_color", label: "Background", default: "#264653" }
                      ].map(color => (
                        <div key={color.key} className="flex items-center gap-3">
                          <input
                            type="color"
                            value={selectedOrg.profile?.branding?.[color.key] || color.default}
                            onChange={(e) => setSelectedOrg(prev => ({
                              ...prev,
                              profile: { 
                                ...prev.profile, 
                                branding: { ...prev.profile?.branding, [color.key]: e.target.value }
                              }
                            }))}
                            className="w-10 h-10 rounded cursor-pointer border-2 border-[#2a9d8f]/50"
                          />
                          <div className="flex-1">
                            <label className="block text-sm text-[#a8c8c4]">{color.label}</label>
                            <Input
                              value={selectedOrg.profile?.branding?.[color.key] || color.default}
                              onChange={(e) => setSelectedOrg(prev => ({
                                ...prev,
                                profile: { 
                                  ...prev.profile, 
                                  branding: { ...prev.profile?.branding, [color.key]: e.target.value }
                                }
                              }))}
                              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] text-sm h-8"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                  
                  <Button
                    onClick={() => updateOrgProfile(selectedOrg.id, selectedOrg.profile)}
                    disabled={saving}
                    className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] text-white"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Branding
                  </Button>
                </div>
              )}

              {/* Scoring Profile Sub-tab */}
              {selectedOrg._activeProfileTab === "scoring" && (
                <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
                  <h3 className="text-lg font-semibold text-[#e9c46a] mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Scoring Profile for {selectedOrg.name}
                  </h3>
                  
                  {/* Profile Selection Dropdown */}
                  <div className="mb-6">
                    <label className="block text-sm text-[#a8c8c4] mb-2">Select Scoring Profile</label>
                    <Select
                      value={selectedOrg.profile?.scoring_profile?.preset_id || "custom"}
                      onValueChange={(value) => {
                        if (value === "custom") {
                          setSelectedOrg(prev => ({
                            ...prev,
                            profile: { 
                              ...prev.profile, 
                              scoring_profile: { 
                                ...prev.profile?.scoring_profile,
                                preset_id: "custom",
                                name: "Custom Profile"
                              }
                            }
                          }));
                        } else {
                          const preset = presets.find(p => p.id === value);
                          if (preset) {
                            setSelectedOrg(prev => ({
                              ...prev,
                              profile: { 
                                ...prev.profile, 
                                scoring_profile: {
                                  preset_id: value,
                                  name: preset.name,
                                  target_skills: preset.target_skills || [],
                                  categories: preset.categories || []
                                }
                              }
                            }));
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]">
                        <SelectValue placeholder="Select a profile" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#264653] border-[#2a9d8f]">
                        <SelectItem value="default" className="text-[#faf3e0] hover:bg-[#2a9d8f]/20">
                          Use Default Profile
                        </SelectItem>
                        {presets.map(preset => (
                          <SelectItem 
                            key={preset.id} 
                            value={preset.id} 
                            className={`hover:bg-[#2a9d8f]/20 ${preset.id === 'custom' ? 'text-[#e9c46a]' : 'text-[#faf3e0]'}`}
                          >
                            {preset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom Profile Settings - Only show if custom selected */}
                  {(selectedOrg.profile?.scoring_profile?.preset_id === "custom" || !selectedOrg.profile?.scoring_profile?.preset_id) && (
                  <>
                  {/* Profile Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm text-[#a8c8c4] mb-1">Profile Name</label>
                      <Input
                        value={selectedOrg.profile?.scoring_profile?.name || ""}
                        onChange={(e) => setSelectedOrg(prev => ({
                          ...prev,
                          profile: { 
                            ...prev.profile, 
                            scoring_profile: { ...prev.profile?.scoring_profile, name: e.target.value }
                          }
                        }))}
                        className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                        placeholder="Custom Profile"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#a8c8c4] mb-1">Target Skills (comma-separated)</label>
                      <Input
                        value={(selectedOrg.profile?.scoring_profile?.target_skills || []).join(", ")}
                        onChange={(e) => setSelectedOrg(prev => ({
                          ...prev,
                          profile: { 
                            ...prev.profile, 
                            scoring_profile: { 
                              ...prev.profile?.scoring_profile, 
                              target_skills: e.target.value.split(",").map(s => s.trim()).filter(s => s)
                            }
                          }
                        }))}
                        className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
                        placeholder="AWS, DevSecOps, Security, ..."
                      />
                    </div>
                  </div>

                  {/* Scoring Categories */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-[#e9c46a]">Scoring Categories</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const categories = selectedOrg.profile?.scoring_profile?.categories || [];
                          setSelectedOrg(prev => ({
                            ...prev,
                            profile: { 
                              ...prev.profile, 
                              scoring_profile: { 
                                ...prev.profile?.scoring_profile, 
                                categories: [...categories, { name: "", keywords: [], weight: 20 }]
                              }
                            }
                          }));
                        }}
                        className="border-[#2a9d8f]/50 text-[#2a9d8f] hover:bg-[#2a9d8f]/20"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {(selectedOrg.profile?.scoring_profile?.categories || []).map((cat, index) => (
                        <div key={index} className="p-3 rounded-lg bg-[#264653]/50 border border-[#2a9d8f]/20">
                          <div className="grid grid-cols-12 gap-3 items-start">
                            <div className="col-span-3">
                              <label className="block text-xs text-[#a8c8c4] mb-1">Category Name</label>
                              <Input
                                value={cat.name || ""}
                                onChange={(e) => {
                                  const cats = [...(selectedOrg.profile?.scoring_profile?.categories || [])];
                                  cats[index] = { ...cats[index], name: e.target.value };
                                  setSelectedOrg(prev => ({
                                    ...prev,
                                    profile: { 
                                      ...prev.profile, 
                                      scoring_profile: { ...prev.profile?.scoring_profile, categories: cats }
                                    }
                                  }));
                                }}
                                className="bg-[#1a3a42] border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
                                placeholder="Cloud"
                              />
                            </div>
                            <div className="col-span-6">
                              <label className="block text-xs text-[#a8c8c4] mb-1">Keywords (comma-separated)</label>
                              <Input
                                value={(cat.keywords || []).join(", ")}
                                onChange={(e) => {
                                  const cats = [...(selectedOrg.profile?.scoring_profile?.categories || [])];
                                  cats[index] = { 
                                    ...cats[index], 
                                    keywords: e.target.value.split(",").map(s => s.trim().toLowerCase()).filter(s => s)
                                  };
                                  setSelectedOrg(prev => ({
                                    ...prev,
                                    profile: { 
                                      ...prev.profile, 
                                      scoring_profile: { ...prev.profile?.scoring_profile, categories: cats }
                                    }
                                  }));
                                }}
                                className="bg-[#1a3a42] border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
                                placeholder="aws, ec2, s3, lambda"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-[#a8c8c4] mb-1">Weight</label>
                              <Input
                                type="number"
                                value={cat.weight || 20}
                                onChange={(e) => {
                                  const cats = [...(selectedOrg.profile?.scoring_profile?.categories || [])];
                                  cats[index] = { ...cats[index], weight: parseInt(e.target.value) || 0 };
                                  setSelectedOrg(prev => ({
                                    ...prev,
                                    profile: { 
                                      ...prev.profile, 
                                      scoring_profile: { ...prev.profile?.scoring_profile, categories: cats }
                                    }
                                  }));
                                }}
                                className="bg-[#1a3a42] border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
                              />
                            </div>
                            <div className="col-span-1 pt-5">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const cats = (selectedOrg.profile?.scoring_profile?.categories || []).filter((_, i) => i !== index);
                                  setSelectedOrg(prev => ({
                                    ...prev,
                                    profile: { 
                                      ...prev.profile, 
                                      scoring_profile: { ...prev.profile?.scoring_profile, categories: cats }
                                    }
                                  }));
                                }}
                                className="text-[#e76f51] hover:bg-[#e76f51]/20 h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {(selectedOrg.profile?.scoring_profile?.categories || []).length === 0 && (
                        <p className="text-[#a8c8c4] text-sm text-center py-4">
                          No scoring categories defined. Click "Add Category" to create one.
                        </p>
                      )}
                    </div>
                  </div>
                  </>
                  )}
                  
                  <Button
                    onClick={() => updateOrgProfile(selectedOrg.id, selectedOrg.profile)}
                    disabled={saving}
                    className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] text-white"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Scoring Profile
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scraper APIs Tab (Super Admin Only) */}
      {activeTab === "scrapers" && isSuperAdmin && (
        <div className="space-y-6">
          <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#e9c46a] flex items-center gap-2">
                <Server className="w-5 h-5" />
                Scraper API Configurations
              </h3>
              <Button
                onClick={() => { setEditingApi(null); setShowApiModal(true); }}
                className="bg-[#2a9d8f] text-white"
                data-testid="add-api-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Scraper API
              </Button>
            </div>
            
            <p className="text-sm text-[#a8c8c4] mb-4">
              Configure API keys and endpoints for job scraping sources. These can be assigned to organizations.
            </p>
            
            {scraperApis.length === 0 ? (
              <p className="text-[#a8c8c4] text-center py-8">No custom scraper APIs configured yet.</p>
            ) : (
              <div className="space-y-3">
                {scraperApis.map(api => (
                  <div key={api.id} className="p-4 rounded-lg bg-[#264653]/50 border border-[#2a9d8f]/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[#faf3e0]">{api.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded ${api.enabled ? "bg-[#2a9d8f]/30 text-[#2a9d8f]" : "bg-[#e76f51]/30 text-[#e76f51]"}`}>
                            {api.enabled ? "Enabled" : "Disabled"}
                          </span>
                          <span className="px-2 py-0.5 text-xs rounded bg-[#264653] text-[#a8c8c4]">
                            {api.api_type}
                          </span>
                        </div>
                        <p className="text-sm text-[#a8c8c4] mt-1">{api.base_url || "No URL configured"}</p>
                        <p className="text-xs text-[#a8c8c4] mt-1">
                          Rate limit: {api.rate_limit} req/hour
                          {api.api_key_value && " • API Key: " + api.api_key_value}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => testScraperApi(api.id)}
                          className="text-[#2a9d8f] hover:bg-[#2a9d8f]/20"
                          data-testid={`test-api-${api.id}`}
                        >
                          <TestTube className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditingApi(api); setShowApiModal(true); }}
                          className="text-[#e9c46a] hover:bg-[#e9c46a]/20"
                          data-testid={`edit-api-${api.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteScraperApi(api.id)}
                          className="text-[#e76f51] hover:bg-[#e76f51]/20"
                          data-testid={`delete-api-${api.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Built-in Scrapers Reference */}
          <div className="p-6 rounded-xl bg-[#1a3a42]/80 border border-[#2a9d8f]/30">
            <h3 className="text-lg font-semibold text-[#e9c46a] mb-4">Built-in Scrapers</h3>
            <p className="text-sm text-[#a8c8c4] mb-4">
              These scrapers are available by default and can be assigned to organizations.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {availableScrapers.filter(s => s.builtin).map(scraper => (
                <div key={scraper.id} className="p-2 rounded bg-[#264653]/50 text-center">
                  <span className="text-sm text-[#faf3e0]">{scraper.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Organization Modal */}
      {showOrgModal && (
        <OrgModal
          org={editingOrg}
          onClose={() => { setShowOrgModal(false); setEditingOrg(null); }}
          onSave={(data) => editingOrg ? updateOrganization(editingOrg.id, data) : createOrganization(data)}
          saving={saving}
        />
      )}

      {/* User Modal */}
      {showUserModal && selectedOrg && (
        <UserModal
          user={editingUser}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
          onSave={(data) => editingUser ? updateOrgUser(selectedOrg.id, editingUser.id, data) : createOrgUser(selectedOrg.id, data)}
          saving={saving}
        />
      )}

      {/* Scraper API Modal */}
      {showApiModal && (
        <ScraperApiModal
          api={editingApi}
          onClose={() => { setShowApiModal(false); setEditingApi(null); }}
          onSave={(data) => editingApi ? updateScraperApi(editingApi.id, data) : createScraperApi(data)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ================== MODAL COMPONENTS ==================

function OrgModal({ org, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: org?.name || "",
    subdomain: org?.subdomain || "",
    description: org?.description || "",
    admin_emails: org?.admin_emails || [],
    user_limit: org?.profile?.user_limit || 10,
    is_active: org?.is_active ?? true
  });
  const [newEmail, setNewEmail] = useState("");

  const addEmail = () => {
    if (newEmail && newEmail.includes("@") && !form.admin_emails.includes(newEmail.toLowerCase())) {
      setForm(prev => ({ ...prev, admin_emails: [...prev.admin_emails, newEmail.toLowerCase()] }));
      setNewEmail("");
    }
  };

  const removeEmail = (email) => {
    setForm(prev => ({ ...prev, admin_emails: prev.admin_emails.filter(e => e !== email) }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a3a42] rounded-xl p-6 w-full max-w-lg mx-4 border border-[#2a9d8f]/30" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-[#faf3e0] mb-4">
          {org ? "Edit Organization" : "New Organization"}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Organization Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              placeholder="Acme Corp"
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Subdomain * (e.g., acme → acme.westbay.io)</label>
            <Input
              value={form.subdomain}
              onChange={e => setForm(prev => ({ ...prev, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              placeholder="acme"
              disabled={!!org}
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Description</label>
            <Textarea
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              rows={2}
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">User Limit</label>
            <Input
              type="number"
              value={form.user_limit}
              onChange={e => setForm(prev => ({ ...prev, user_limit: parseInt(e.target.value) || 10 }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Org Admin Emails</label>
            <div className="space-y-2 mb-2">
              {form.admin_emails.map((email, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded bg-[#264653]/50">
                  <span className="flex-1 text-[#faf3e0] text-sm">{email}</span>
                  <button onClick={() => removeEmail(email)} className="text-[#e76f51]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                placeholder="admin@acme.com"
                onKeyDown={e => e.key === "Enter" && addEmail()}
              />
              <Button type="button" onClick={addEmail} className="bg-[#2a9d8f] text-white">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {org && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded"
              />
              <label className="text-sm text-[#a8c8c4]">Active</label>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="border-[#2a9d8f]/50 text-[#a8c8c4]">
            Cancel
          </Button>
          <Button 
            onClick={() => onSave(form)} 
            disabled={saving || !form.name || !form.subdomain}
            className="bg-[#2a9d8f] text-white"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ user, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || "user",
    permissions: user?.permissions || ["view_leads"],
    is_active: user?.is_active ?? true
  });

  const togglePermission = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a3a42] rounded-xl p-6 w-full max-w-md mx-4 border border-[#2a9d8f]/30" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-[#faf3e0] mb-4">
          {user ? "Edit User" : "Add User"}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Email *</label>
            <Input
              value={form.email}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              disabled={!!user}
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Role</label>
            <select
              value={form.role}
              onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}
              className="w-full p-2 rounded bg-[#264653] border border-[#2a9d8f]/50 text-[#faf3e0]"
            >
              {USER_ROLES.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Permissions</label>
            <div className="space-y-2">
              {PERMISSIONS.map(perm => (
                <label key={perm.value} className="flex items-center gap-2 text-sm text-[#faf3e0] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(perm.value)}
                    onChange={() => togglePermission(perm.value)}
                    className="rounded"
                  />
                  {perm.label}
                </label>
              ))}
            </div>
          </div>
          
          {user && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded"
              />
              <label className="text-sm text-[#a8c8c4]">Active</label>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="border-[#2a9d8f]/50 text-[#a8c8c4]">
            Cancel
          </Button>
          <Button 
            onClick={() => onSave(form)} 
            disabled={saving || !form.name || !form.email}
            className="bg-[#2a9d8f] text-white"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function ScraperApiModal({ api, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: api?.name || "",
    api_type: api?.api_type || "rapidapi",
    base_url: api?.base_url || "",
    api_key_name: api?.api_key_name || "",
    api_key_value: "",
    enabled: api?.enabled ?? true,
    rate_limit: api?.rate_limit || 100
  });
  const [showKey, setShowKey] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a3a42] rounded-xl p-6 w-full max-w-md mx-4 border border-[#2a9d8f]/30" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-[#faf3e0] mb-4">
          {api ? "Edit Scraper API" : "Add Scraper API"}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              placeholder="My Custom API"
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">API Type</label>
            <select
              value={form.api_type}
              onChange={e => setForm(prev => ({ ...prev, api_type: e.target.value }))}
              className="w-full p-2 rounded bg-[#264653] border border-[#2a9d8f]/50 text-[#faf3e0]"
            >
              <option value="rapidapi">RapidAPI</option>
              <option value="direct">Direct API</option>
              <option value="scraper">Web Scraper</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Base URL</label>
            <Input
              value={form.base_url}
              onChange={e => setForm(prev => ({ ...prev, base_url: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              placeholder="https://api.example.com/jobs"
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">API Key Header Name</label>
            <Input
              value={form.api_key_name}
              onChange={e => setForm(prev => ({ ...prev, api_key_name: e.target.value }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
              placeholder="X-RapidAPI-Key"
            />
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">API Key Value</label>
            <div className="flex gap-2">
              <Input
                type={showKey ? "text" : "password"}
                value={form.api_key_value}
                onChange={e => setForm(prev => ({ ...prev, api_key_value: e.target.value }))}
                className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] flex-1"
                placeholder={api ? "Leave blank to keep existing" : "Enter API key"}
              />
              <Button type="button" variant="outline" onClick={() => setShowKey(!showKey)} className="border-[#2a9d8f]/50">
                {showKey ? <EyeOff className="w-4 h-4 text-[#a8c8c4]" /> : <Eye className="w-4 h-4 text-[#a8c8c4]" />}
              </Button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-[#a8c8c4] mb-1">Rate Limit (requests/hour)</label>
            <Input
              type="number"
              value={form.rate_limit}
              onChange={e => setForm(prev => ({ ...prev, rate_limit: parseInt(e.target.value) || 100 }))}
              className="bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0]"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
              className="rounded"
            />
            <label className="text-sm text-[#a8c8c4]">Enabled</label>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="border-[#2a9d8f]/50 text-[#a8c8c4]">
            Cancel
          </Button>
          <Button 
            onClick={() => onSave(form)} 
            disabled={saving || !form.name || !form.api_type}
            className="bg-[#2a9d8f] text-white"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
